import type { Feature, Geometry, Polygon } from "geojson";
import iso from "iso-3166-1";

import type { IIdentity } from "@/providers/auth-provider";
import type { CuratorActivityRow, HitlDominationRow } from "@/types/tina";

export const FORMER_UID = "__former__";
export const FORMER_LABEL = "Former curators";

export interface CuratorStat {
  uid: string;
  displayName: string;
  photoUrl: string | null;
  isFormer: boolean;
  count: number;
}

export interface CountryDomination {
  countryCode: string; // upper-case ISO alpha-2
  numericId: string; // 3-digit ISO numeric string, matches world-atlas feature.id
  countryName: string;
  total: number;
  curators: CuratorStat[]; // sorted by count desc
}

export interface LeaderboardEntry {
  uid: string;
  displayName: string;
  photoUrl: string | null;
  isFormer: boolean;
  total: number;
  countryCodes: string[]; // known countries only, by per-country count desc
  /**
   * Mean resolves on a day they actually worked — and "worked" means 10 or more resolves
   * on that Athens day, tina's `0073` floor. Before that floor this was a median over
   * every day, the 1-resolve ones included, because a mean over those would report a
   * curator's lightest days as their pace. The floor is what lets the mean be honest.
   *
   * null when the curator never had such a day, and null for the folded "Former curators"
   * bucket, whose row is several curators combined — a mean of means is not a mean.
   */
  avgDay: number | null;
  /** Resolves in the trailing 30 days. null for the same reason as `avgDay`. */
  last30d: number | null;
  /**
   * The curator's biggest single day and the Athens date it was set, or null when the
   * activity read has no row for them. One nullable object rather than two fields, so
   * "a count with no date" cannot be represented.
   */
  bestDay: { count: number; day: string } | null;
  /**
   * Resolves on the curator's newest active day — but only when that day IS
   * today, else 0. The server sends the DAY rather than a stored "resolved
   * today" count, because the matview behind it refreshes once per cron
   * round-robin and a baked count would still read yesterday's number just
   * after midnight. The comparison has to happen here, against the browser's
   * own idea of today in the ops timezone.
   */
  todayCount: number;
}

/**
 * `YYYY-MM-DD` for an instant, in the ops timezone (Europe/Athens) — the same
 * wall clock Tina buckets `last_resolved_day` on. `en-CA` is the locale whose
 * short date format IS ISO order, so this needs no manual reassembly.
 */
export function athensDate(instant: Date, timeZone = "Europe/Athens"): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(instant);
}

const MONTH_ABBREVIATIONS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * `29 Jun 2026` from the server's `YYYY-MM-DD`. Built from the string's own parts, never
 * through a `Date`: parsing "2026-06-29" as an instant lands at midnight UTC and renders
 * as the 28th for anyone west of Greenwich — and the whole reason the server sends a DAY
 * rather than a timestamp is that no timezone is meant to touch it. A value that does not
 * split returns unchanged, so a bad row shows something rather than an empty cell.
 */
export function formatRecordDay(iso: string): string {
  const [year, month, day] = iso.split("-");
  const name = MONTH_ABBREVIATIONS[Number(month) - 1];
  if (!year || !day || !name) return iso;
  return `${Number(day)} ${name} ${year}`;
}

export interface DominationData {
  countries: CountryDomination[]; // known countries only, sorted by total desc
  leaderboard: LeaderboardEntry[]; // sorted by total desc
  grandTotal: number; // every resolve, incl. unknown countries + former curators
  colorByUid: Map<string, string>; // stable, distinct colour per curator
  record: OneDayRecord | null; // null when nobody on the board has a best day
}

/**
 * The one-day record: the biggest single day any curator on the board has had, and who
 * holds it. A shape of its own rather than a flag on an entry, because the holder need
 * not be ranked near the top — a curator can own the biggest day on the board and still
 * sit low on all-time volume, which is exactly who the crown exists to surface.
 */
export interface OneDayRecord {
  entry: LeaderboardEntry;
  /** The record itself, so a caller need not reach through `entry.bestDay`. */
  count: number;
  /** `YYYY-MM-DD`, Athens — the day the record was set. */
  day: string;
}

// Neutral grey for the folded "Former curators" entry — they don't earn a hue.
const FORMER_COLOR = "oklch(0.62 0.02 264)";
// Golden-angle hue stepping gives maximally-separated, stable colours: a given
// rank always maps to the same hue, and adding a curator never reshuffles the
// others. L/C tuned to read clearly on the dark map and as avatar rings.
const COLOR_BASE_HUE = 24;
const COLOR_GOLDEN_ANGLE = 137.508;

export function curatorColorAt(index: number): string {
  const hue =
    (((COLOR_BASE_HUE + index * COLOR_GOLDEN_ANGLE) % 360) + 360) % 360;
  return `oklch(0.72 0.17 ${hue.toFixed(1)})`;
}

// One colour per curator, keyed by uid, assigned in leaderboard (rank) order.
export function buildCuratorColors(
  leaderboard: LeaderboardEntry[],
): Map<string, string> {
  const colors = new Map<string, string>();
  let i = 0;
  for (const entry of leaderboard) {
    colors.set(entry.uid, entry.isFormer ? FORMER_COLOR : curatorColorAt(i++));
  }
  return colors;
}

interface Profile {
  uid: string;
  displayName: string;
  photoUrl: string | null;
  isFormer: boolean;
}

function profileFor(uid: string, byId: Map<string, IIdentity>): Profile {
  const m = byId.get(uid);
  if (!m) {
    return {
      uid: FORMER_UID,
      displayName: FORMER_LABEL,
      photoUrl: null,
      isFormer: true,
    };
  }
  return {
    uid: m.id,
    displayName: m.displayName ?? m.email ?? m.id,
    photoUrl: m.photoUrl ?? null,
    isFormer: false,
  };
}

export function buildDomination(
  rows: HitlDominationRow[],
  activity: CuratorActivityRow[],
  members: IIdentity[],
  now: Date,
): DominationData {
  const byId = new Map(members.map((m) => [m.id, m]));
  const activityByUid = new Map(activity.map((a) => [a.resolved_by, a]));

  // Per-country curator counts (former curators folded into one stat).
  const countryAgg = new Map<string, Map<string, CuratorStat>>();
  // Leaderboard totals + per-(curator, country) counts for flag ordering, plus the
  // activity triplet. A curator's rows are per COUNTRY, so their newest day and its
  // volume have to be folded across rows: the max day wins, and rows sharing it sum.
  const lbAgg = new Map<
    string,
    LeaderboardEntry & {
      perCountry: Map<string, number>;
      lastDay: string;
      onLastDay: number;
    }
  >();
  let grandTotal = 0;

  for (const r of rows) {
    grandTotal += r.resolve_count;
    const p = profileFor(r.resolved_by, byId);
    const country = iso.whereAlpha2(r.country_code);

    let lb = lbAgg.get(p.uid);
    if (!lb) {
      lb = {
        ...p,
        total: 0,
        countryCodes: [],
        avgDay: null,
        last30d: null,
        todayCount: 0,
        bestDay: null,
        perCountry: new Map(),
        // Empty, not this row's day: the fold below must see this row as the
        // first sighting and take its count. Seeding it here would double it.
        lastDay: "",
        onLastDay: 0,
      };
      lbAgg.set(p.uid, lb);
    }
    lb.total += r.resolve_count;
    if (r.last_resolved_day > lb.lastDay) {
      lb.lastDay = r.last_resolved_day;
      lb.onLastDay = r.resolved_on_last_day;
    } else if (r.last_resolved_day === lb.lastDay) {
      lb.onLastDay += r.resolved_on_last_day;
    }
    if (country) {
      const cc = country.alpha2.toUpperCase();
      lb.perCountry.set(cc, (lb.perCountry.get(cc) ?? 0) + r.resolve_count);

      let curators = countryAgg.get(cc);
      if (!curators) {
        curators = new Map();
        countryAgg.set(cc, curators);
      }
      const stat = curators.get(p.uid);
      if (stat) stat.count += r.resolve_count;
      else curators.set(p.uid, { ...p, count: r.resolve_count });
    }
  }

  const countries: CountryDomination[] = [...countryAgg.entries()]
    .map(([cc, curators]) => {
      const country = iso.whereAlpha2(cc)!;
      const sorted = [...curators.values()].sort((a, b) => b.count - a.count);
      return {
        countryCode: cc,
        numericId: country.numeric.padStart(3, "0"),
        countryName: country.country,
        total: sorted.reduce((s, c) => s + c.count, 0),
        curators: sorted,
      };
    })
    .sort((a, b) => b.total - a.total);

  const today = athensDate(now);
  const leaderboard: LeaderboardEntry[] = [...lbAgg.values()]
    .map(({ perCountry, lastDay, onLastDay, ...entry }) => {
      const activity = activityByUid.get(entry.uid);
      return {
        ...entry,
        avgDay: activity?.avg_day ?? null,
        last30d: activity?.last_30d ?? null,
        todayCount: lastDay === today ? onLastDay : 0,
        // Both halves of the one-day fact come from the same row, so they are set
        // together or not at all.
        bestDay: activity
          ? { count: activity.best_day, day: activity.best_day_date }
          : null,
        countryCodes: [...perCountry.entries()]
          .sort((a, b) => b[1] - a[1])
          .map(([cc]) => cc),
      };
    })
    .sort((a, b) => b.total - a.total);

  return {
    countries,
    leaderboard,
    grandTotal,
    colorByUid: buildCuratorColors(leaderboard),
    record: oneDayRecord(leaderboard),
  };
}

/**
 * Who holds the one-day record. Former curators cannot hold it by construction: the
 * activity read is keyed by real uid, and the folded "Former curators" bucket has no uid
 * to key on — so this is "the best current member", the rule the crown follows, since a
 * record that cannot be shown next to a name is not worth a crown.
 *
 * A tie on the day goes to the curator with more work behind it, and the uid settles a
 * full tie, so the crown cannot wobble between two reads of the same data.
 */
function oneDayRecord(leaderboard: LeaderboardEntry[]): OneDayRecord | null {
  let record: OneDayRecord | null = null;
  for (const entry of leaderboard) {
    if (!entry.bestDay) continue;
    const { count } = entry.bestDay;
    const wins =
      record == null ||
      count > record.count ||
      (count === record.count &&
        (entry.total > record.entry.total ||
          (entry.total === record.entry.total &&
            entry.uid < record.entry.uid)));
    if (wins) record = { entry, count, day: entry.bestDay.day };
  }
  return record;
}

export interface BubblePlacement {
  r: number;
  curator: CuratorStat;
  isLeader: boolean;
  // screen-space offset from the country anchor (constant at any zoom)
  dx: number;
  dy: number;
}

// Only the leading curator is drawn inside each country, so the map shows a
// single bubble per country — its top curator. Everyone else stays folded into
// the country's total and tint; the full per-curator breakdown still surfaces on
// hover. (Raise this to stack runner-up markers again.)
export const MAX_MARKERS_PER_COUNTRY = 1;
const MIN_LEADER_RADIUS = 11;
const MAX_LEADER_RADIUS = 26;
const AREA_TO_RADIUS = 0.24;

/**
 * Size the leading marker to the country's rendered size (its projected area in
 * px²), clamped so it stays readable on tiny nations and not overbearing on
 * huge ones. Runner-up markers derive from this, keeping a constant size ratio.
 */
export function leaderRadiusForArea(projectedAreaPx: number): number {
  const r = AREA_TO_RADIUS * Math.sqrt(Math.max(0, projectedAreaPx));
  return Math.max(MIN_LEADER_RADIUS, Math.min(MAX_LEADER_RADIUS, r));
}

// Bubble covers at most this fraction of its country's projected area. The
// readability floor in {@link leaderRadiusForArea} would otherwise let a marker
// blanket a small, compact nation (e.g. the Netherlands) and spill past its
// jagged coast even while staying inside the bounding box.
const AREA_CONTAIN_FRACTION = 0.5;

/**
 * Cap a leader marker so it always sits inside the country it marks — at every
 * zoom level, since bubble and map share one projection and scale together, so a
 * fit at base zoom holds when zoomed out too. Two independent ceilings apply,
 * whichever is smaller:
 *  - by area: at most {@link AREA_CONTAIN_FRACTION} of the country's projected
 *    area, so the bubble can't blanket a small, compact nation (e.g. NL).
 *  - by extent: its diameter never exceeds the country's smaller projected side
 *    (`countryMinExtent`, px), so it can't spill across a long, thin one
 *    (e.g. Chile).
 */
export function fitLeaderRadius(
  idealRadius: number,
  projectedAreaPx: number,
  countryMinExtent: number,
): number {
  const byArea = Math.sqrt(
    (AREA_CONTAIN_FRACTION * Math.max(0, projectedAreaPx)) / Math.PI,
  );
  const byExtent = Math.max(0, countryMinExtent) / 2;
  return Math.min(idealRadius, byArea, byExtent);
}

/**
 * Reduce a country to its single largest landmass, dropping far-flung
 * territories (e.g. French Guiana for FR). Keeping them would render France in
 * South America, stretch the whole-world projection to fit it, and drag the
 * area centroid out of the country (FR's lands in Spain). Applied up front so
 * rendering, projection fit, anchoring and containment all agree on the main
 * part only. `area` is injected (d3's geoArea) to stay pure and testable;
 * single-Polygon countries are returned unchanged.
 */
export function toMainlandFeature(
  feature: Feature<Geometry>,
  area: (geom: Geometry) => number,
): Feature<Geometry> {
  const geom = feature.geometry;
  if (geom.type !== "MultiPolygon") return feature;
  let best: { area: number; coordinates: Polygon["coordinates"] } | null = null;
  for (const coordinates of geom.coordinates) {
    const a = area({ type: "Polygon", coordinates });
    if (!best || a > best.area) best = { area: a, coordinates };
  }
  return best
    ? {
        ...feature,
        geometry: { type: "Polygon", coordinates: best.coordinates },
      }
    : feature;
}

/**
 * Lay out a country's curator markers as screen-space offsets from the country
 * anchor (its centroid). Only the leading curator (highest count) is drawn — see
 * {@link MAX_MARKERS_PER_COUNTRY} — placed directly on the anchor, which is
 * inside the country, so there's no border to test. The full per-curator
 * breakdown still surfaces on hover.
 */
export function layoutCountryBubbles(
  curators: CuratorStat[],
  leaderRadius: number,
): BubblePlacement[] {
  return curators
    .filter((c) => !c.isFormer)
    .slice(0, MAX_MARKERS_PER_COUNTRY)
    .map((curator) => ({
      r: leaderRadius,
      curator,
      isLeader: true,
      dx: 0,
      dy: 0,
    }));
}

export interface RankedEntry {
  entry: LeaderboardEntry;
  rank: number;
}

export interface TopConquerors {
  top: RankedEntry[];
  pinned: RankedEntry | null; // the signed-in curator when outside the top
}

export function topConquerors(
  leaderboard: LeaderboardEntry[],
  youUid: string | null,
  limit = 8,
): TopConquerors {
  const ranked = leaderboard.map((entry, i) => ({ entry, rank: i + 1 }));
  const top = ranked.slice(0, limit);
  const you = youUid ? ranked.find((r) => r.entry.uid === youUid) : undefined;
  const pinned = you && you.rank > limit ? you : null;
  return { top, pinned };
}
